import{useEffect,useMemo,useRef,useState}from'react'
import{fighters}from'./content'
import{registerHeroAnimationInventory,registeredHeroClip}from'./heroAnimationRegistry'
import{appendAttackHistory,deriveHeroCombatPresentation}from'./heroCombatRuntime'
import{selectSignatureCue,type HeroCombatId,type SignatureAttackKind}from'./heroCombatIdentity'
import{deriveHeroPerformancePacket,movementModeForAerial,targetScaleForHero,type HeroPerformancePacket}from'./heroPerformanceRuntime'
import{useFightStore}from'./store'
import type{AttackPhase}from'./simulation'

export type HeroCombatSide='player'|'enemy'
type AnimationInventoryEventDetail={heroId:string;clipNames:readonly string[]}

export type HeroSignatureEventDetail={
  side:HeroCombatSide
  heroId:HeroCombatId
  attack:SignatureAttackKind
  skillName:string
  animationVariant:string
  resolvedClip:string|null
  clipConfidence:string
  trail:string
  impact:string
  performance:HeroPerformancePacket
}

export const HERO_SIGNATURE_EVENT='inyron:hero-signature'
export const HERO_ANIMATION_INVENTORY_EVENT='inyron:hero-animation-inventory'

function useHeroCombatSide(side:HeroCombatSide){
  const index=useFightStore(s=>side==='player'?s.playerIndex:s.enemyIndex)
  const attackPhase=useFightStore(s=>side==='player'?s.player.attackPhase:s.enemy.attackPhase)
  const lastAttack=useFightStore(s=>side==='player'?s.player.lastAttack:s.enemy.lastAttack)
  const comboCount=useFightStore(s=>side==='player'?s.player.comboCount:s.enemy.comboCount)
  const heroId=fighters[index].id as HeroCombatId
  const[history,setHistory]=useState<SignatureAttackKind[]>([])
  const[performance,setPerformance]=useState<HeroPerformancePacket|null>(null)
  const previousPhase=useRef<AttackPhase>(null)

  useEffect(()=>{setHistory([]);setPerformance(null);previousPhase.current=null},[heroId])

  useEffect(()=>{
    const enteredStartup=attackPhase==='startup'&&previousPhase.current!=='startup'&&lastAttack!==null
    if(enteredStartup){
      setHistory(current=>appendAttackHistory(current,lastAttack))
      const cue=selectSignatureCue(heroId,lastAttack,comboCount)
      const resolved=registeredHeroClip(heroId,lastAttack)
      const snapshot=useFightStore.getState(),actor=side==='player'?snapshot.player:snapshot.enemy,target=side==='player'?snapshot.enemy:snapshot.player,targetIndex=side==='player'?snapshot.enemyIndex:snapshot.playerIndex
      const targetScale=targetScaleForHero(fighters[targetIndex].id),finisherEligible=target.maxHealth>0&&target.health>0&&target.health/target.maxHealth<=.34
      const nextPerformance=deriveHeroPerformancePacket({heroId,attack:lastAttack,comboCount,movementMode:movementModeForAerial(actor.aerialMode),attackPhase:'startup',targetScale,finisherEligible})
      setPerformance(nextPerformance)
      const detail:HeroSignatureEventDetail={side,heroId,attack:lastAttack,skillName:cue.skillName,animationVariant:cue.animationVariant,resolvedClip:resolved.clip,clipConfidence:resolved.confidence,trail:cue.trail,impact:cue.impact,performance:nextPerformance}
      window.dispatchEvent(new CustomEvent<HeroSignatureEventDetail>(HERO_SIGNATURE_EVENT,{detail}))
    }
    previousPhase.current=attackPhase
  },[attackPhase,lastAttack,comboCount,heroId,side])

  const activeAttack=attackPhase===null?null:lastAttack
  const presentation=useMemo(()=>deriveHeroCombatPresentation(heroId,activeAttack,comboCount,history),[heroId,activeAttack,comboCount,history])
  const resolved=activeAttack?registeredHeroClip(heroId,activeAttack):{clip:null,confidence:'fallback' as const,score:0}
  return{presentation,attackPhase,resolved,performance}
}

const cardStyle=(side:HeroCombatSide,performance:HeroPerformancePacket|null):React.CSSProperties=>({
  position:'fixed',bottom:94,[side==='player'?'left':'right']:18,zIndex:28,width:'min(310px,43vw)',pointerEvents:'none',padding:'9px 11px',border:`1px solid rgba(130,220,255,${performance?Math.min(.52,.18+performance.intensity*.1):.18})`,background:'linear-gradient(180deg,rgba(7,14,25,.72),rgba(5,9,16,.46))',backdropFilter:'blur(7px)',boxShadow:`0 10px ${performance?30+performance.intensity*8:30}px rgba(0,0,0,.24)`,textAlign:side==='player'?'left':'right'
})

const hintLabel=(attack:SignatureAttackKind)=>attack==='light'?'LIGHT':attack==='heavy'?'HEAVY':attack==='special'?'SPECIAL':'THROW'

function HeroSignatureCard({side}:{side:HeroCombatSide}){
  const{presentation,attackPhase,resolved,performance}=useHeroCombatSide(side)
  const active=attackPhase!==null&&presentation.skillName!==null
  const hints=presentation.nextHints.map(hintLabel).join(' · ')
  return <aside
    className={`hero-signature-card hero-signature-card--${side}`}
    aria-live="polite"
    aria-label={`${side} hero combat identity`}
    data-testid={`${side}-hero-combat`}
    data-hero-id={presentation.heroId}
    data-skill={presentation.skillName??''}
    data-animation-variant={presentation.animationVariant??''}
    data-resolved-clip={resolved.clip??''}
    data-clip-confidence={resolved.confidence}
    data-trail={presentation.trail??''}
    data-impact={presentation.impact??''}
    data-performance-variant={performance?.variantId??''}
    data-power-state={performance?.powerState??''}
    data-motion-cue={performance?.motionCue??''}
    data-movement-mode={performance?.movementMode??''}
    style={cardStyle(side,performance)}
  >
    <div style={{font:'700 7px Orbitron,sans-serif',letterSpacing:'.18em',opacity:.62}}>{side==='player'?'P1 SIGNATURE':'RIVAL SIGNATURE'} · {presentation.stance}</div>
    <strong style={{display:'block',marginTop:4,font:'800 11px Orbitron,sans-serif',letterSpacing:'.08em',opacity:active?1:.58}}>{active?presentation.skillName:'COMBAT READY'}</strong>
    <div style={{marginTop:4,font:'600 7px Orbitron,sans-serif',letterSpacing:'.08em',opacity:.5}}>{presentation.comboName?`CHAIN · ${presentation.comboName}`:`RHYTHM · ${presentation.rhythm}`}</div>
    {hints&&<div style={{marginTop:3,font:'600 7px Orbitron,sans-serif',letterSpacing:'.06em',opacity:.42}}>NEXT · {hints}</div>}
    {active&&<div style={{marginTop:3,font:'600 7px ui-monospace,monospace',opacity:.44}}>{resolved.clip?`GLB · ${resolved.clip}`:presentation.animationVariant} · {presentation.trail}/{presentation.impact}</div>}
    {performance&&<div style={{marginTop:3,font:'700 7px Orbitron,sans-serif',letterSpacing:'.07em',opacity:.58}}>POWER · {performance.powerState.toUpperCase()} · {performance.movementMode.toUpperCase()} · {performance.motionCue.toUpperCase()}</div>}
  </aside>
}

export function HeroCombatRuntimeBridge(){
  useEffect(()=>{
    type BridgeHealth={active:boolean;inventoryEvents:number;lastHeroId:string|null;updatedAt:number}
    const runtimeWindow=window as typeof window&{__INYRON_HERO_COMBAT_BRIDGE__?:BridgeHealth}
    const health:BridgeHealth={active:true,inventoryEvents:0,lastHeroId:null,updatedAt:performance.now()}
    runtimeWindow.__INYRON_HERO_COMBAT_BRIDGE__=health
    const onInventory=(event:Event)=>{
      const detail=(event as CustomEvent<AnimationInventoryEventDetail>).detail
      if(!detail||typeof detail.heroId!=='string'||!Array.isArray(detail.clipNames))return
      try{registerHeroAnimationInventory(detail.heroId,detail.clipNames)}catch{return}
      health.inventoryEvents++;health.lastHeroId=detail.heroId;health.updatedAt=performance.now()
      runtimeWindow.__INYRON_HERO_COMBAT_BRIDGE__={...health}
    }
    window.addEventListener(HERO_ANIMATION_INVENTORY_EVENT,onInventory)
    return()=>{
      window.removeEventListener(HERO_ANIMATION_INVENTORY_EVENT,onInventory)
      runtimeWindow.__INYRON_HERO_COMBAT_BRIDGE__={...health,active:false,updatedAt:performance.now()}
    }
  },[])
  return <><HeroSignatureCard side="player"/><HeroSignatureCard side="enemy"/></>
}
